import React, { useState, useEffect, Suspense } from 'react';
import SysOwnerSideBar from './SysOwnerSidebar';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import ErrorBoundary from '../../common/ErrorBoundery';
import LogoLoader from '../../common/LogoLoader';
import appService from '../../../services/ApplicationService';
import userService from '../../../services/UserService';
import uarService from '../../../services/UARService';
import CustomSpeedDial from '../../common/SpeedDial';
import Modal from '../../common/Modal';
import MultipleSelect from '../../common/MultipleSelect';
import NormalTextField from '../../common/NormalTextField';
import DynamicSnackbar from '../../common/Snackbar';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { alpha } from '@mui/material/styles';

// Icons
import RateReviewIcon from '@mui/icons-material/RateReview';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewListIcon from '@mui/icons-material/ViewList';
import BusinessIcon from '@mui/icons-material/Business';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
    Dashboard as DashboardIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon
} from '@mui/icons-material';

const globalStyles = `
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
    }
    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideIn {
        from { transform: translateX(-10px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    @keyframes progressAnimation {
        0% { width: 0%; }
        100% { width: 100%; }
    }
`;

const DataTable = React.lazy(() => import('../../common/DataGrid'));

const UserAccessReview = () => {
    const navigate = useNavigate();
    const [apps, setApps] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem('uarSearchTerm') || '';
    });
    const [selectedStatus, setSelectedStatus] = useState(() => {
        return localStorage.getItem('uarSelectedStatus') || 'all';
    });
    const [viewMode, setViewMode] = useState(() => {
        const savedMode = localStorage.getItem('uarViewMode');
        return savedMode || 'flat';
    });

    // Year filter state
    const [selectedYear, setSelectedYear] = useState(() => {
        const savedYear = localStorage.getItem('uarSelectedYear');
        return savedYear ? parseInt(savedYear) : new Date().getFullYear();
    });

    // State for selected app details
    const [selectedApp, setSelectedApp] = useState(null);
    const [selectedAppDetails, setSelectedAppDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    
    // Store the expanded app IDs in state
    const [expandedAppIds, setExpandedAppIds] = useState(() => {
        // Initialize from localStorage if available
        const saved = localStorage.getItem('uarExpandedAppIds');
        return saved ? JSON.parse(saved) : [];
    });
    
    // UAR related states
    const [uar, setUAR] = useState([]);
    const [uarExist, setUARExist] = useState(false);
    const [uarFile, setUARFile] = useState({});
    const [selectedFrequency, setSelectedFrequency] = useState(null);
    
    // UI control state
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openSetupModal, setOpenSetupModal] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [saved, setSaved] = useState(false);

    // Get the valid start date if it exists
    const startDate = dayjs(uarFile.START_DATE);
    const isValidStartDate = startDate.isValid();

    // Save expanded app IDs to localStorage when they change
    useEffect(() => {
        localStorage.setItem('uarExpandedAppIds', JSON.stringify(expandedAppIds));
    }, [expandedAppIds]);

    // Save search term to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('uarSearchTerm', searchTerm);
    }, [searchTerm]);

    // Save selected status to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('uarSelectedStatus', selectedStatus);
    }, [selectedStatus]);
    
    // Save view mode to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('uarViewMode', viewMode);
    }, [viewMode]);

    // Save selected year to localStorage when it changes
    useEffect(() => {
        localStorage.setItem('uarSelectedYear', selectedYear.toString());
    }, [selectedYear]);

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = globalStyles;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                
                // Add a short delay before fetching data
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                const currentUser = await userService.fetchCurrentUser();
                if (currentUser) {
                    const assignedApps = await appService.fetchAppsByOwner(currentUser.id);
                    
                    if (assignedApps) {
                        setApps(assignedApps);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData(); // Initial fetch
    }, []);

    // Effect to fetch expanded app details
    useEffect(() => {
        // Function to load data for all expanded apps
        const loadExpandedAppsData = async () => {
            // Skip if no expanded apps or no apps loaded yet
            if (expandedAppIds.length === 0 || apps.length === 0) return;
            
            // Find apps that match the expanded IDs
            const appsToLoad = apps.filter(app => expandedAppIds.includes(app.id));
            
            // Load each app's data if needed
            for (const app of appsToLoad) {
                await loadAppData(app);
            }
        };
        
        loadExpandedAppsData();
    }, [expandedAppIds, apps]);
    
    // Add an effect to reload data when saved state changes
    useEffect(() => {
        // Only run if not first render and we have a selected app
        if (selectedApp) {
            console.log("Reloading data after save operation for app:", selectedApp.APP_NAME);
            // Use setTimeout to ensure the state update has completed
            setTimeout(async () => {
                try {
                    // Directly fetch UAR records for this app
                    const uarRecords = await uarService.fetchUARByApp(selectedApp.id);
                    console.log("UAR records fetched after save:", uarRecords);
                    
                    // Filter records to only include those for this specific app
                    const filteredRecords = uarRecords.filter(record => 
                        record.APP_NAME === selectedApp.id || 
                        record.APP_ID === selectedApp.id
                    );
                    
                    console.log(`Filtered ${uarRecords.length} records to ${filteredRecords.length} for app ${selectedApp.id}`);
                    
                    if (filteredRecords && filteredRecords.length > 0) {
                        // Store the UAR records for this app
                        setUAR(prev => ({
                            ...prev, 
                            [selectedApp.id]: filteredRecords
                        }));
                        
                        // Mark this app as having UAR records
                        setUARExist(prev => ({
                            ...prev,
                            [selectedApp.id]: true
                        }));
                    }
                    
                    // Full reload of the app data
                    await loadAppData(selectedApp);
                } catch (error) {
                    console.error("Error reloading data after save:", error);
                }
            }, 500);
        }
    }, [saved, selectedApp]);
    
    // Function to load app data
    const loadAppData = async (app) => {
        if (!app) return;
        
        setLoadingDetails(true);
        console.log(`Loading data for app ${app.id}...`);
        
        try {
            // Fetch application details
            const appDetails = await appService.fetchAppsById(app.id);
            if (appDetails) {
                setSelectedAppDetails(prev => ({
                    ...prev,
                    [app.id]: appDetails
                }));
            }
            
            // Fetch UAR records for this app
            console.log(`Fetching UAR records for app ${app.id}...`);
            const uarRecords = await uarService.fetchUARByApp(app.id);
            console.log(`UAR records received for app ${app.id}:`, uarRecords);
            
            // Filter records to only include those for this specific app
            const filteredRecords = uarRecords.filter(record => 
                record.APP_NAME === app.id || 
                record.APP_ID === app.id
            );
            
            console.log(`Filtered ${uarRecords.length} records to ${filteredRecords.length} for app ${app.id}`);
            
            if (filteredRecords && filteredRecords.length > 0) {
                // Store the UAR records for this app
                setUAR(prev => ({
                    ...prev, 
                    [app.id]: filteredRecords
                }));
                
                // Mark this app as having UAR records
                setUARExist(prev => ({
                    ...prev,
                    [app.id]: true
                }));
                
                console.log(`App ${app.id} has ${filteredRecords.length} UAR records`);
            } else {
                // No records found for this app
                setUARExist(prev => ({
                    ...prev,
                    [app.id]: false
                }));
                
                // Initialize empty array for this app
                setUAR(prev => ({
                    ...prev,
                    [app.id]: []
                }));
                
                console.log(`App ${app.id} has no UAR records`);
            }
        } catch (error) {
            console.error(`Error fetching data for app ${app.id}:`, error);
        } finally {
            setLoadingDetails(false);
        }
    };

    // Toggle expanded state for an app
    const toggleAppExpanded = async (app) => {
        if (expandedAppIds.includes(app.id)) {
            // App is currently expanded, collapse it
            setExpandedAppIds(expandedAppIds.filter(id => id !== app.id));
        } else {
            // App is currently collapsed, expand it
            setExpandedAppIds([...expandedAppIds, app.id]);
            // Load data for this app if needed
            await loadAppData(app);
        }
    };

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleStatusChange = (event) => {
        setSelectedStatus(event.target.value);
    };

    const toggleViewMode = () => {
        setViewMode(viewMode === 'flat' ? 'grouped' : 'flat');
    };

    // Select an app to view details
    const handleAppClick = (app) => {
        toggleAppExpanded(app);
    };

    // Go back to the apps list
    const handleBackToList = () => {
        setSelectedApp(null);
        setSelectedAppDetails(null);
        setUAR([]);
        setUARExist(false);
        setUARFile({});
        setSelectedFrequency(null);
        setOpenCreateModal(false);
        setOpenSetupModal(false);
    };

    // Handle UAR setup
    const handleCreateUAR = (appId) => {
        setSelectedApp(apps.find(app => app.id === appId));
        
        // Set default date to today
        const today = dayjs();
        const formattedToday = today.format('YYYY-MM-DD');
        
        // Initialize UAR file with default date 
        setUARFile({
            START_DATE: formattedToday,
            DAYS_TO_COMPLETE: '30' // Set default days to complete
        });
        
        setOpenCreateModal(true);
    };

    // Handle modal closures
    const handleCloseCreateModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenCreateModal(true);
        } else {
            setOpenCreateModal(false);
            // Don't clear selectedApp as it's needed for the workflow
        }
    };

    const handleCloseSetupModal = (event, reason) => {
        if (reason === 'backdropClick') {
            setOpenSetupModal(true);
        } else {
            setOpenSetupModal(false);
            // Don't clear selectedApp as it might be needed for subsequent operations
        }
    };

    // Proceed to setup
    const createUAR = (e) => {
        e.preventDefault();
        
        // Set default date to today when opening setup modal
        const today = dayjs();
        const formattedToday = today.format('YYYY-MM-DD');
        
        // Update the uarFile with default date
        setUARFile(prev => ({
            ...prev,
            START_DATE: formattedToday
        }));
        
        setOpenCreateModal(false);
        setOpenSetupModal(true);
    };

    // Handle frequency selection
    const handleFrequencyChange = (selectedOptions) => {
        const frequency = selectedOptions.value;
        setSelectedFrequency(selectedOptions);

        // Find the app details to include both ID and name
        const app = apps.find(app => app.id === selectedApp?.id);
        console.log("Selected app for UAR:", app);

        // Make sure we have a valid start date
        let currentStartDate = uarFile.START_DATE;
        if (!currentStartDate || !dayjs(currentStartDate).isValid()) {
            currentStartDate = dayjs().format('YYYY-MM-DD');
            console.log("Setting default date in frequency selection:", currentStartDate);
        }

        const updatedData = {
            ...uarFile,
            APP_ID: selectedApp?.id,
            APP_NAME: selectedApp?.id,
            FREQUENCY: frequency,
            START_DATE: currentStartDate // Ensure we preserve or set a valid date
        };

        console.log("Updated uarFile data:", updatedData);
        setUARFile(updatedData);
    };

    // Handle date selection
    const handleDateChange = (newValue) => {
        // Check if newValue is a valid Day.js object
        if (!newValue || !newValue.isValid()) {
            console.error("Invalid date selected:", newValue);
            
            // Clear the date in uarFile
            const updatedData = {
                ...uarFile,
                START_DATE: null
            };
            setUARFile(updatedData);
            return;
        }
        
        // Format date strictly as YYYY-MM-DD
        const formattedDate = newValue.format('YYYY-MM-DD');
        console.log("Formatted date from datepicker:", formattedDate);
        
        // Validate the formatted date
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(formattedDate)) {
            console.error("Date format validation failed for:", formattedDate);
            return;
        }

        const updatedData = {
            ...uarFile,
            START_DATE: formattedDate
        };

        setUARFile(updatedData);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Check if it's the START_DATE and format it accordingly
        let formattedValue = value;
        if (name === 'START_DATE' && value) {
            // If the field is START_DATE, treat it as a date and format it
            if (dayjs(value).isValid()) {
                formattedValue = dayjs(value).format('YYYY-MM-DD');
                console.log("Date formatted in handleInputChange:", formattedValue);
                
                // Validate the date format
                const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                if (!dateRegex.test(formattedValue)) {
                    console.error("Date format validation failed in handleInputChange:", formattedValue);
                    
                    // Try to fix the date format by using native Date
                    try {
                        const date = new Date(value);
                        if (!isNaN(date.getTime())) {
                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, '0');
                            const day = String(date.getDate()).padStart(2, '0');
                            formattedValue = `${year}-${month}-${day}`;
                            console.log("Date reformatted using native Date:", formattedValue);
                        } else {
                            console.error("Could not parse date with native Date:", value);
                            formattedValue = null;
                        }
                    } catch (error) {
                        console.error("Error parsing date in handleInputChange:", error);
                        formattedValue = null;
                    }
                }
            } else {
                console.error("Invalid date in handleInputChange:", value);
                formattedValue = null;
            }
        }

        const updatedData = {
            ...uarFile,
            [name]: formattedValue
        };

        console.log(`Updated ${name} field to:`, formattedValue);
        setUARFile(updatedData);
    };

    // Create UAR data records
    const createUARData = async (e) => {
        e.preventDefault();
        try {
            console.log("========================");
            console.log("CREATE UAR DATA STARTING");
            console.log("Selected App:", selectedApp);
            console.log("UAR File State:", uarFile);
            console.log("=======================");
            
            // Force a default date if none is set
            if (!uarFile.START_DATE) {
                const defaultDate = dayjs().format('YYYY-MM-DD');
                console.log("No date set, using today:", defaultDate);
                
                // Update the uarFile with the default date
                setUARFile(prev => ({
                    ...prev,
                    START_DATE: defaultDate
                }));
                
                // Create a temporary startDate to continue the process
                let startDate = new Date();
                
                // Show a notification that we're using today's date
                setSnackbarMessage('Using today as the start date');
                setSnackbarSeverity('info');
                setSnackbarOpen(true);
                
                // Wait a moment for state to update
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            // Ensure the date is in the correct format
            let dateToUse = uarFile.START_DATE;
            if (dateToUse && !dateToUse.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // Try to format it
                try {
                    const dateObj = dayjs(dateToUse);
                    if (dateObj.isValid()) {
                        dateToUse = dateObj.format('YYYY-MM-DD');
                        console.log("Reformatted date:", dateToUse);
                        
                        // Update the uarFile with the formatted date
                        setUARFile(prev => ({
                            ...prev,
                            START_DATE: dateToUse
                        }));
                        
                        // Wait a moment for state to update
                        await new Promise(resolve => setTimeout(resolve, 100));
                    } else {
                        throw new Error("Invalid date");
                    }
                } catch (dateError) {
                    console.error("Could not format date:", dateError);
                    setSnackbarMessage('Please select a valid date in YYYY-MM-DD format');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }
            }
            
            console.log("Using start date:", dateToUse);
            
            // Create a Date object from the dateToUse string for calculations of future dates
            let startDate = new Date(dateToUse);
            
            // Function to format a Date object to YYYY-MM-DD
            const formatDate = (date) => {
                if (!date || isNaN(date.getTime())) {
                    console.error("Invalid date provided to formatDate:", date);
                    return null;
                }
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Add a validation step for the start date
            if (!startDate || isNaN(startDate.getTime())) {
                console.error("Invalid start date:", startDate);
                setSnackbarMessage('Invalid start date format. Please use YYYY-MM-DD format.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return; // Stop execution if date is invalid
            }

            // Function to add months to the start date based on the frequency
            const addMonthsToDate = (date, months) => {
                const newDate = new Date(date);
                newDate.setMonth(newDate.getMonth() + months);
                return newDate;
            };

            let recordsToCreate = [];
            
            // Get existing UAR records for this app to determine appropriate REVIEW_TAG values
            const existingUarRecords = uar[selectedApp.id] || [];
            console.log("Existing UAR records for app:", existingUarRecords);
            
            let existingTags = {};
            
            // Create a map of existing tags by frequency
            if (existingUarRecords.length > 0) {
                existingUarRecords.forEach(record => {
                    if (!existingTags[record.FREQUENCY]) {
                        existingTags[record.FREQUENCY] = [];
                    }
                    existingTags[record.FREQUENCY].push(parseInt(record.REVIEW_TAG, 10));
                });
            }
            
            // Function to get the next available tag number for a frequency
            const getNextTagNumber = (frequency) => {
                if (!existingTags[frequency]) {
                    return 1; // No records for this frequency, start with 1
                }
                
                // Find the highest tag number and add 1
                const maxTag = Math.max(...existingTags[frequency]);
                return maxTag + 1;
            };

            // For One-time reviews, we need to ensure they have unique tags
            const getOneTimeTag = () => {
                // If we have any existing one-time reviews, find the max tag and add 1
                if (existingTags['One-time'] && existingTags['One-time'].length > 0) {
                    const maxTag = Math.max(...existingTags['One-time']);
                    return maxTag + 1;
                }
                return 1; // No existing one-time reviews
            };

            // Based on frequency, determine how many records to create and assign REVIEW_TAG
            if (uarFile.FREQUENCY === 'One-time') {
                // For one-time review, just create a single record with unique tag
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: dateToUse, // Use the original string directly instead of formatting again
                    REVIEW_TAG: getOneTimeTag(),
                    // Ensure APP_NAME is set to the app ID (UUID) as required by backend
                    APP_ID: selectedApp?.id,
                    APP_NAME: selectedApp?.id
                });
            } else if (uarFile.FREQUENCY === 'Annually') {
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: dateToUse, // Use the original string directly
                    REVIEW_TAG: getNextTagNumber('Annually'), // Use next available tag
                    // Ensure APP_NAME is set to the app ID (UUID) as required by backend
                    APP_ID: selectedApp?.id,
                    APP_NAME: selectedApp?.id
                });
            } else if (uarFile.FREQUENCY === 'Semi-Annually') {
                // Create two records, 6 months apart
                const baseTag = getNextTagNumber('Semi-Annually');
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: dateToUse, // Use the original string directly
                    REVIEW_TAG: baseTag,
                    // Ensure APP_NAME is set to the app ID (UUID) as required by backend
                    APP_ID: selectedApp?.id,
                    APP_NAME: selectedApp?.id
                });
                let secondRecordDate = addMonthsToDate(startDate, 6);
                recordsToCreate.push({
                    ...uarFile,
                    START_DATE: formatDate(secondRecordDate),
                    REVIEW_TAG: baseTag + 1, // Increment tag for second record
                    // Ensure APP_NAME is set to the app ID (UUID) as required by backend
                    APP_ID: selectedApp?.id,
                    APP_NAME: selectedApp?.id
                });
            } else if (uarFile.FREQUENCY === 'Quarterly') {
                // Create 4 records, each 3 months apart
                const baseTag = getNextTagNumber('Quarterly');
                for (let i = 0; i < 4; i++) {
                    if (i === 0) {
                        // First record uses the original date string
                        recordsToCreate.push({
                            ...uarFile,
                            START_DATE: dateToUse,
                            REVIEW_TAG: baseTag,
                            APP_ID: selectedApp?.id,
                            APP_NAME: selectedApp?.id
                        });
                    } else {
                        let recordDate = addMonthsToDate(startDate, i * 3);
                        recordsToCreate.push({
                            ...uarFile,
                            START_DATE: formatDate(recordDate),
                            REVIEW_TAG: baseTag + i,
                            APP_ID: selectedApp?.id,
                            APP_NAME: selectedApp?.id
                        });
                    }
                }
            } else if (uarFile.FREQUENCY === 'Monthly') {
                // Create 12 records, each 1 month apart
                const baseTag = getNextTagNumber('Monthly');
                for (let i = 0; i < 12; i++) {
                    if (i === 0) {
                        // First record uses the original date string
                        recordsToCreate.push({
                            ...uarFile,
                            START_DATE: dateToUse,
                            REVIEW_TAG: baseTag,
                            APP_ID: selectedApp?.id,
                            APP_NAME: selectedApp?.id
                        });
                    } else {
                        let recordDate = addMonthsToDate(startDate, i);
                        recordsToCreate.push({
                            ...uarFile,
                            START_DATE: formatDate(recordDate),
                            REVIEW_TAG: baseTag + i,
                            APP_ID: selectedApp?.id,
                            APP_NAME: selectedApp?.id
                        });
                    }
                }
            }

            // Now create the UAR records using the service for each calculated date
            let createdCount = 0;
            let errorCount = 0;
            
            for (let record of recordsToCreate) {
                try {
                    // Validate record has required fields before sending
                    if (!record.APP_NAME || !record.START_DATE || !record.FREQUENCY || !record.DAYS_TO_COMPLETE) {
                        console.error("Record missing required fields:", record);
                        errorCount++;
                        continue;
                    }
                    
                    // Final validation of date format
                    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
                    if (!dateRegex.test(record.START_DATE)) {
                        console.error("Invalid date format in record:", record.START_DATE);
                        
                        // Try to fix the date format one last time
                        try {
                            const date = new Date(record.START_DATE);
                            if (!isNaN(date.getTime())) {
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                record.START_DATE = `${year}-${month}-${day}`;
                            } else {
                                console.error("Could not parse date:", record.START_DATE);
                                errorCount++;
                                continue; // Skip this record
                            }
                        } catch (dateError) {
                            console.error("Error fixing date format:", dateError);
                            errorCount++;
                            continue; // Skip this record
                        }
                    }
                    
                    console.log('Attempting to create UAR record with data:', JSON.stringify(record));
                    const result = await uarService.createUAR(record);
                    console.log('Raw result from createUAR:', result);
                    
                    // Check if the result contains an error
                    if (result && result.error) {
                        console.warn('Warning creating UAR record:', result.error);
                        // Skip this record if it's a conflict (already exists)
                        if (result.error.includes('already exists')) {
                            console.log('Skipping duplicate record:', record.START_DATE, 'REVIEW_TAG:', record.REVIEW_TAG);
                            continue;
                        }
                        // For other errors, increment error count
                        errorCount++;
                    } else {
                        // Record created successfully
                        console.log('UAR record created successfully:', record.START_DATE, 'REVIEW_TAG:', record.REVIEW_TAG);
                        createdCount++;
                    }
                } catch (err) {
                    console.error('Error in UAR record creation:', err);
                    errorCount++;
                }
            }
            
            setOpenSetupModal(false); // Close modal after completion
            setSaved(!saved); // Toggle saved state to trigger a refresh
            
            // Reload the app data to reflect the changes
            if (selectedApp) {
                await loadAppData(selectedApp);
            }
            
            // Show appropriate success or partial success message
            if (createdCount > 0 && errorCount === 0) {
                if (uarFile.FREQUENCY === 'One-time') {
                    setSnackbarMessage('One-time review created successfully');
                } else {
                    setSnackbarMessage('User access review schedule created successfully');
                }
                setSnackbarSeverity('success');
            } else if (createdCount > 0 && errorCount > 0) {
                setSnackbarMessage(`${createdCount} reviews created with ${errorCount} errors`);
                setSnackbarSeverity('warning');
            } else if (createdCount === 0 && errorCount > 0) {
                setSnackbarMessage('Failed to create access review schedule');
                setSnackbarSeverity('error');
            } else {
                setSnackbarMessage('No new reviews created. They may already exist.');
                setSnackbarSeverity('info');
            }
            setSnackbarOpen(true);

        } catch (error) {
            console.error("There was a problem creating UAR file", error);
            setSnackbarMessage('Error creating access review schedule');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // View review details
    const handleViewReview = (appId, uarID) => {
        navigate(`/useraccessreview/data/${appId}/${uarID}`);
    };

    // Available frequency options
    const frequency = [
        { value: 'One-time', label: 'One-time Review' },
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Semi-Annually', label: 'Semi-Annually' },
        { value: 'Annually', label: 'Annually' },
    ];

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const filteredApps = apps.filter(app => {
        const matchesSearch = app.APP_NAME && app.APP_NAME.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (selectedStatus === 'all') return matchesSearch;
        // Add more filter conditions if needed
        
        return matchesSearch;
    });

    // Group apps by company
    const groupedApps = React.useMemo(() => {
        const groups = {};
        
        filteredApps.forEach(app => {
            const companyName = app.COMPANY_NAME || 'Unassigned';
            if (!groups[companyName]) {
                groups[companyName] = [];
            }
            groups[companyName].push(app);
        });
        
        // Convert to array format for easier rendering
        return Object.entries(groups).map(([company, apps]) => ({
            company,
            apps
        })).sort((a, b) => a.company.localeCompare(b.company));
    }, [filteredApps]);

    // Render an individual application card
    const renderAppCard = (app) => {
        const isExpanded = expandedAppIds.includes(app.id);
        const appUarRecords = uar[app.id] || [];
        const hasUar = uarExist[app.id] || false;
        const isLoading = loadingDetails && isExpanded;

        // Debug log
        if (isExpanded) {
            console.log(`Rendering expanded app card for ${app.APP_NAME} (ID: ${app.id})`);
            console.log(`UAR records for ${app.APP_NAME}:`, appUarRecords);
            console.log(`hasUar flag for ${app.APP_NAME}:`, hasUar);
        }

        // Group records by year and sort by start date
        const recordsByYear = {};
        
        // Sort the records by start date
        const sortedRecords = [...appUarRecords].sort((a, b) => {
            const dateA = new Date(a.START_DATE);
            const dateB = new Date(b.START_DATE);
            return dateA - dateB;
        });
        
        // Group sorted records by year
        sortedRecords.forEach(record => {
            if (!record.START_DATE) return;
            
            const year = new Date(record.START_DATE).getFullYear();
            if (!recordsByYear[year]) {
                recordsByYear[year] = [];
            }
            recordsByYear[year].push(record);
        });
        
        // Sort years in descending order (newest first)
        const sortedYears = Object.keys(recordsByYear).sort((a, b) => b - a);

        return (
            <mui.Paper
                key={app.id}
                elevation={0}
                sx={{
                    overflow: 'hidden',
                    borderRadius: 2,
                    backgroundColor: '#ffffff',
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                    }
                }}
            >
                <mui.Box 
                    sx={{ 
                        p: 2.5, 
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: { xs: 'flex-start', md: 'center' },
                        gap: 2
                    }}
                >
                    <mui.Box sx={{ flexGrow: 1 }}>
                        <mui.Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 600,
                                mb: 0.5,
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {app.APP_NAME || 'Unnamed Application'}
                        </mui.Typography>
                        <mui.Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'text.secondary',
                                mb: 1.5
                            }}
                        >
                            {app.APP_DESCRIPTION || 'No description available'} 
                            {viewMode === 'flat' && (
                                <> • <strong>{app.COMPANY_NAME || 'No company'}</strong></>
                            )}
                        </mui.Typography>
                    </mui.Box>
                    
                    <mui.Button
                        variant={isExpanded ? "contained" : "outlined"}
                        startIcon={isExpanded ? <KeyboardBackspaceIcon /> : <FolderOpenIcon />}
                        color="primary"
                        sx={{
                            borderRadius: 2,
                            px: 2,
                            whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleAppClick(app)}
                    >
                        {isExpanded ? 'Close Details' : 'View Reviews'}
                    </mui.Button>
                </mui.Box>
                
                <mui.Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <mui.Box sx={{ p: 2.5, pt: 0, animation: 'fadeIn 0.3s ease-out' }}>
                        <mui.Divider sx={{ mb: 2.5 }} />
                        
                        {isLoading ? (
                            <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <mui.CircularProgress size={40} />
                                <mui.Typography sx={{ ml: 2 }}>Loading review data...</mui.Typography>
                            </mui.Box>
                        ) : (
                            <>
                                {hasUar ? (
                                    <mui.Box>
                                        <mui.Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2, 
                                            flexWrap: 'wrap', 
                                            gap: 1
                                        }}>
                                            <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Scheduled Access Reviews
                                            </mui.Typography>
                                        
                                            {/* Year filter chips */}
                                            <mui.Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    gap: 1,
                                                    flexWrap: 'wrap' 
                                                }}
                                            >
                                                {(() => {
                                                    // Get unique years from records
                                                    const years = new Set();
                                                    appUarRecords.forEach(record => {
                                                        if (record.START_DATE) {
                                                            const year = new Date(record.START_DATE).getFullYear();
                                                            years.add(year);
                                                        }
                                                    });
                                                    
                                                    // Convert to array and sort
                                                    const yearArray = Array.from(years).sort();
                                                    
                                                    // Current year and adjacent years
                                                    const currentYear = new Date().getFullYear();
                                                    
                                                    // If there are no records for this year, add current year to the array
                                                    if (!yearArray.includes(currentYear)) {
                                                        yearArray.push(currentYear);
                                                    }
                                                    
                                                    // Special value for "All Years"
                                                    const ALL_YEARS = -1;
                                                    
                                                    // Create a function to save selected app's year
                                                    const handleYearClick = (year) => {
                                                        // Save the selection for this specific app
                                                        localStorage.setItem(`uarSelectedYear_${app.id}`, year.toString());
                                                        setSelectedYear(year);
                                                    };
                                                    
                                                    // Get the persisted year for this specific app or fall back to the general one
                                                    const appSelectedYear = parseInt(localStorage.getItem(`uarSelectedYear_${app.id}`)) || selectedYear;
                                                    
                                                    // Prepare all chips to display
                                                    return (
                                                        <>
                                                            <mui.Chip
                                                                key="all"
                                                                label="All Years"
                                                                variant={appSelectedYear === ALL_YEARS ? "filled" : "outlined"}
                                                                color={appSelectedYear === ALL_YEARS ? "primary" : "default"}
                                                                onClick={() => handleYearClick(ALL_YEARS)}
                                                                sx={{
                                                                    borderRadius: 1.5,
                                                                    fontWeight: 600,
                                                                    transition: 'all 0.2s',
                                                                    '&.MuiChip-filled': {
                                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                    }
                                                                }}
                                                            />
                                                            {yearArray.sort((a, b) => b - a).map(year => (
                                                                <mui.Chip
                                                                    key={year}
                                                                    label={year}
                                                                    variant={appSelectedYear === year ? "filled" : "outlined"}
                                                                    color={appSelectedYear === year ? "primary" : "default"}
                                                                    onClick={() => handleYearClick(year)}
                                                                    sx={{
                                                                        borderRadius: 1.5,
                                                                        fontWeight: 600,
                                                                        transition: 'all 0.2s',
                                                                        '&.MuiChip-filled': {
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                                        }
                                                                    }}
                                                                />
                                                            ))}
                                                        </>
                                                    );
                                                })()}
                                            </mui.Box>
                                        </mui.Box>
                                        
                                        <Suspense fallback={
                                            <mui.Box sx={{ p: 4, textAlign: 'center' }}>
                                                <mui.CircularProgress size={40} />
                                                <mui.Typography sx={{ mt: 2 }}>Loading review data...</mui.Typography>
                                            </mui.Box>
                                        }>
                                            {(() => {
                                                // Get the persisted year for this specific app or fall back to the general one
                                                const appSelectedYear = parseInt(localStorage.getItem(`uarSelectedYear_${app.id}`)) || selectedYear;
                                                
                                                // Get only UAR records for this specific app
                                                const appSpecificRecords = appUarRecords.filter(record => 
                                                    record.APP_NAME === app.id || 
                                                    record.APP_ID === app.id
                                                );
                                                
                                                console.log(`Filtering ${appUarRecords.length} records for app ${app.APP_NAME} (${app.id}):`, appSpecificRecords);
                                                
                                                // Filter records by selected year
                                                const recordsForYear = appSelectedYear === -1 
                                                    ? appSpecificRecords // Show all records if All Years is selected 
                                                    : appSpecificRecords.filter(record => {
                                                        if (!record.START_DATE) return false;
                                                        const year = new Date(record.START_DATE).getFullYear();
                                                        return year === appSelectedYear;
                                                    });
                                                
                                                // Sort by start date
                                                const sortedRecords = [...recordsForYear].sort((a, b) => {
                                                    const dateA = new Date(a.START_DATE);
                                                    const dateB = new Date(b.START_DATE);
                                                    return dateA - dateB;
                                                });
                                                
                                                // Create rows for the table
                                                const rows = sortedRecords.map((record, index) => ({
                                                    id: `${appSelectedYear}-${index}`,
                                                    index: index + 1,
                                                    FREQUENCY: record.FREQUENCY || '-',
                                                    REVIEW_TAG: record.REVIEW_TAG || '-',
                                                    START_DATE: record.START_DATE || '-',
                                                    STATUS: record.STATUS || 'Pending',
                                                    uarID: record.id,
                                                    appId: app.id
                                                }));
                                                
                                                if (rows.length > 0) {
                                                    return (
                                                        <DataTable
                                                            rows={rows}
                                                            columns={columns}
                                                            columnsWithActions={renderColumnsWithActions(app.id)}
                                                            pageSize={5}
                                                            rowsPerPageOptions={[5, 10, 25]}
                                                            disableSelectionOnClick
                                                            sx={{
                                                                '& .MuiDataGrid-columnHeader[data-field="index"]': {
                                                                    maxWidth: '60px !important',
                                                                    minWidth: '60px !important',
                                                                },
                                                                '& .MuiDataGrid-cell[data-field="index"]': {
                                                                    maxWidth: '60px !important',
                                                                    minWidth: '60px !important',
                                                                }
                                                            }}
                                                        />
                                                    );
                                                } else {
                                                    return (
                                                        <mui.Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                                            {appSelectedYear === -1 
                                                                ? "No reviews scheduled for this application."
                                                                : `No reviews scheduled for ${appSelectedYear}.`
                                                            }
                                                        </mui.Typography>
                                                    );
                                                }
                                            })()}
                                        </Suspense>
                                        
                                        <mui.Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'flex-end', 
                                            mt: 2, 
                                            gap: 1.5,
                                            pt: 1.5,
                                            borderTop: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            
                                            <mui.Button 
                                                variant="contained" 
                                                startIcon={<AddIcon />}
                                                onClick={() => handleCreateUAR(app.id)}
                                                sx={{
                                                    borderRadius: 1.5,
                                                    fontWeight: 500,
                                                    boxShadow: '0 3px 6px rgba(0,0,0,0.08)',
                                                    '&:hover': {
                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
                                                    }
                                                }}
                                            >
                                                Schedule New Review
                                            </mui.Button>
                                        </mui.Box>
                                    </mui.Box>
                                ) : (
                                    <mui.Box sx={{ 
                                        textAlign: 'center',
                                        py: 4,
                                        borderRadius: 2,
                                        backgroundColor: alpha('#f0f4f8', 0.5),
                                        border: '1px dashed',
                                        borderColor: 'divider'
                                    }}>
                                        <EditCalendarIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.7, mb: 2 }} />
                                        <mui.Typography variant="h6" sx={{ mb: 1 }}>
                                            No Access Reviews Scheduled
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            There are no user access reviews scheduled for this application yet.
                                        </mui.Typography>
                                        <mui.Button 
                                            variant="contained" 
                                            startIcon={<AddIcon />}
                                            onClick={() => handleCreateUAR(app.id)}
                                        >
                                            Schedule Access Review
                                        </mui.Button>
                                    </mui.Box>
                                )}
                            </>
                        )}
                    </mui.Box>
                </mui.Collapse>
            </mui.Paper>
        );
    };

    // Table display for UAR records
    const getStatusChip = (status) => {
        switch(status) {
            case 'Completed':
                return (
                    <mui.Chip 
                        label="Completed" 
                        size="small" 
                        color="success" 
                        icon={<CheckCircleIcon />} 
                    />
                );
            case 'In Progress':
                return (
                    <mui.Chip 
                        label="In Progress" 
                        size="small" 
                        color="primary" 
                        icon={<HourglassEmptyIcon />} 
                    />
                );
            default:
                return (
                    <mui.Chip 
                        label="Pending" 
                        size="small" 
                        color="default" 
                        icon={<AccessTimeIcon />} 
                        variant="outlined"
                    />
                );
        }
    };

    // Table columns
    const columns = [
        { 
            field: 'index', 
            headerName: '#', 
            width: 60, 
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => (
                <mui.Box 
                    sx={{ 
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <mui.Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 600,
                            color: 'text.secondary'
                        }}
                    >
                        {params.value}
                    </mui.Typography>
                </mui.Box>
            )
        },
        { field: 'FREQUENCY', headerName: 'Frequency', flex: 1 },
        { field: 'REVIEW_TAG', headerName: 'Review Cycle', flex: 1 },
        { 
            field: 'START_DATE', 
            headerName: 'Start Date', 
            flex: 1,
            renderCell: (params) => (
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarMonthIcon sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                    {params.value}
                </mui.Box>
            )
        },
        { 
            field: 'STATUS', 
            headerName: 'Status', 
            flex: 1,
            renderCell: (params) => getStatusChip(params.value)
        },
    ];

    // Actions column
    const renderActionButton = (appId) => (params) => {
        return (
            <mui.Button
                variant="contained"
                size="small"
                startIcon={<FolderOpenIcon fontSize="small" />}
                onClick={() => handleViewReview(appId, params.row.uarID)}
                sx={{ 
                    borderRadius: '8px',
                    textTransform: 'none',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                    background: 'linear-gradient(90deg, #3949ab 0%, #4f66d8 100%)',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 4px 8px rgba(73, 86, 221, 0.3)',
                        background: 'linear-gradient(90deg, #3949ab 0%, #5c73e6 100%)',
                        transform: 'translateY(-1px)'
                    }
                }}
            >
                View Details
            </mui.Button>
        );
    };

    // Create columns with actions for a specific app
    const renderColumnsWithActions = (appId) => [
        ...columns,
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            renderCell: renderActionButton(appId),
        },
    ];

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">User Access Review</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <RateReviewIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                User Access Review
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Create and view status of user access reviews for your applications
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 3,
                        minHeight: '80vh',
                        boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
                        p: 3
                    }}>
                        <mui.Paper 
                            elevation={0} 
                            sx={{ 
                                p: 2, 
                                mb: 3, 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                gap: 2,
                                backgroundColor: '#ffffff',
                                borderRadius: 2
                            }}
                        >
                            <mui.Box sx={{ position: 'relative', flexGrow: 1 }}>
                                <SearchIcon sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'text.secondary' }} />
                                <mui.InputBase
                                    placeholder="Search applications..."
                                    value={searchTerm}
                                    onChange={handleSearch}
                                    sx={{
                                        pl: 5,
                                        pr: 2,
                                        py: 1,
                                        width: '100%',
                                        backgroundColor: '#f5f7fa',
                                        borderRadius: 2,
                                        '&:hover': {
                                            backgroundColor: '#eef1f6',
                                        }
                                    }}
                                />
                            </mui.Box>
                            
                            <mui.Box sx={{ display: 'flex', gap: 2, flexWrap: 'nowrap' }}>
                                <mui.ButtonGroup 
                                    variant="outlined" 
                                    size="small"
                                    sx={{ 
                                        borderRadius: 2,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <mui.Tooltip title="Show all applications">
                                        <mui.Button 
                                            onClick={() => setViewMode('flat')}
                                            variant={viewMode === 'flat' ? 'contained' : 'outlined'}
                                            sx={{ borderRadius: 0 }}
                                        >
                                            <ViewListIcon fontSize="small" />
                                        </mui.Button>
                                    </mui.Tooltip>
                                    <mui.Tooltip title="Group by company">
                                        <mui.Button 
                                            onClick={() => setViewMode('grouped')}
                                            variant={viewMode === 'grouped' ? 'contained' : 'outlined'}
                                            sx={{ borderRadius: 0 }}
                                        >
                                            <BusinessIcon fontSize="small" />
                                        </mui.Button>
                                    </mui.Tooltip>
                                </mui.ButtonGroup>
                            
                                <mui.FormControl sx={{ minWidth: 180 }}>
                                    <mui.Select
                                        value={selectedStatus}
                                        onChange={handleStatusChange}
                                        displayEmpty
                                        size="small"
                                        sx={{ 
                                            backgroundColor: '#f5f7fa',
                                            '& .MuiSelect-select': { 
                                                py: 1,
                                                display: 'flex',
                                                alignItems: 'center'
                                            }
                                        }}
                                        startAdornment={<FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                                    >
                                        <mui.MenuItem value="all">All Applications</mui.MenuItem>
                                        <mui.MenuItem value="active">Active Reviews</mui.MenuItem>
                                        <mui.MenuItem value="pending">Pending Reviews</mui.MenuItem>
                                    </mui.Select>
                                </mui.FormControl>
                            </mui.Box>
                        </mui.Paper>
                        
                        {isLoading ? (
                            <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                <LogoLoader 
                                    size={80} 
                                    message="Loading Applications..." 
                                    duration={1.5} 
                                />
                            </mui.Box>
                        ) : filteredApps.length === 0 ? (
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: '#ffffff',
                                    borderRadius: 2
                                }}
                            >
                                <mui.Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                                    No applications found
                                </mui.Typography>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {searchTerm ? "Try adjusting your search criteria" : "No applications have been assigned to you yet"}
                                </mui.Typography>
                            </mui.Paper>
                        ) : (
                            viewMode === 'flat' ? (
                                <mui.Stack spacing={2}>
                                    {filteredApps.map((app) => renderAppCard(app))}
                                </mui.Stack>
                            ) : (
                                <mui.Stack spacing={4}>
                                    {groupedApps.map(({ company, apps }) => (
                                        <mui.Box key={company}>
                                            <mui.Box 
                                                sx={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    mb: 2,
                                                    pb: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider'
                                                }}
                                            >
                                                <BusinessIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                                                <mui.Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                                    {company}
                                                </mui.Typography>
                                                <mui.Chip 
                                                    label={`${apps.length} ${apps.length === 1 ? 'Application' : 'Applications'}`} 
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                />
                                            </mui.Box>
                                            <mui.Stack spacing={2}>
                                                {apps.map((app) => renderAppCard(app))}
                                            </mui.Stack>
                                        </mui.Box>
                                    ))}
                                </mui.Stack>
                            )
                        )}
                    </mui.Box>

                    {/* Modals for UAR creation */}
                    <Modal
                        open={openCreateModal}
                        onClose={handleCloseCreateModal}
                        header=""
                        body={
                            <mui.Box sx={{ 
                                p: 1,
                                pt: 3,
                                position: 'relative', 
                                overflow: 'hidden',
                                textAlign: 'center'
                            }}>
                                {/* Background decorative elements */}
                                <mui.Box 
                                    sx={{ 
                                        position: 'absolute', 
                                        top: -20, 
                                        right: -20, 
                                        width: 100, 
                                        height: 100, 
                                        borderRadius: '50%', 
                                        background: 'linear-gradient(135deg, rgba(73, 86, 221, 0.1), rgba(73, 86, 221, 0))',
                                        zIndex: 0
                                    }} 
                                />
                                <mui.Box 
                                    sx={{ 
                                        position: 'absolute', 
                                        bottom: -30, 
                                        left: -30, 
                                        width: 150, 
                                        height: 150, 
                                        borderRadius: '50%', 
                                        background: 'linear-gradient(135deg, rgba(73, 86, 221, 0.05), rgba(73, 86, 221, 0))',
                                        zIndex: 0
                                    }} 
                                />

                                {/* Card content with relative positioning to appear above background */}
                                <mui.Box sx={{ position: 'relative', zIndex: 1 }}>
                                    <mui.Avatar 
                                        sx={{ 
                                            width: 80, 
                                            height: 80, 
                                            bgcolor: 'rgba(73, 86, 221, 0.08)', 
                                            mx: 'auto',
                                            mb: 3,
                                            '& .MuiSvgIcon-root': {
                                                fontSize: 40,
                                                color: 'primary.main'
                                            }
                                        }}
                                    >
                                        <EditCalendarIcon />
                                    </mui.Avatar>

                                    <mui.Typography 
                                        variant="h5" 
                                        component="h2" 
                                        sx={{ 
                                            mb: 1.5, 
                                            fontWeight: 600, 
                                            color: 'text.primary' 
                                        }}
                                    >
                                        Access Review Setup
                                    </mui.Typography>

                                    <mui.Typography 
                                        variant="body1" 
                                        sx={{ 
                                            mb: 4, 
                                            color: 'text.secondary',
                                            maxWidth: '90%',
                                            mx: 'auto'
                                        }}
                                    >
                                        No access review schedule exists for <mui.Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>{selectedApp?.APP_NAME}</mui.Box>. 
                                        Set up regular reviews to maintain compliance and security.
                                    </mui.Typography>

                                    <mui.Box 
                                        sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'center', 
                                            gap: 2, 
                                            mt: 2 
                                        }}
                                    >
                                        <mui.Button 
                                            onClick={handleCloseCreateModal} 
                                            variant="outlined"
                                            color="inherit"
                                            sx={{ 
                                                borderRadius: 2,
                                                px: 3
                                            }}
                                        >
                                            Cancel
                                        </mui.Button>
                                        <mui.Button 
                                            onClick={createUAR} 
                                            variant="contained" 
                                            startIcon={<SettingsIcon />}
                                            sx={{ 
                                                borderRadius: 2, 
                                                px: 3,
                                                boxShadow: '0 4px 10px rgba(73, 86, 221, 0.2)',
                                                background: 'linear-gradient(90deg, #3949ab 0%, #4f66d8 100%)',
                                                '&:hover': {
                                                    background: 'linear-gradient(90deg, #3949ab 0%, #5c73e6 100%)',
                                                    boxShadow: '0 6px 15px rgba(73, 86, 221, 0.3)',
                                                    transform: 'translateY(-1px)'
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            Configure Schedule
                                        </mui.Button>
                                    </mui.Box>
                                </mui.Box>
                            </mui.Box>
                        }
                        footer={null}
                    />

                    <Modal
                        size="sm"
                        open={openSetupModal}
                        onClose={handleCloseSetupModal}
                        header=""
                        body={
                            <mui.Box sx={{ 
                                p: 3,
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                {/* Background decorative elements */}
                                <mui.Box 
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        right: 0, 
                                        width: '40%', 
                                        height: 6, 
                                        background: 'linear-gradient(90deg, rgba(73, 86, 221, 0), rgba(73, 86, 221, 0.3))',
                                        zIndex: 0
                                    }} 
                                />
                                <mui.Box 
                                    sx={{ 
                                        position: 'absolute', 
                                        top: 0, 
                                        left: 0, 
                                        width: '40%', 
                                        height: 6, 
                                        background: 'linear-gradient(90deg, rgba(73, 86, 221, 0.3), rgba(73, 86, 221, 0))',
                                        zIndex: 0
                                    }} 
                                />

                                <mui.Typography 
                                    variant="h5" 
                                    component="h2"
                                    align="center"
                                    sx={{ 
                                        fontWeight: 600, 
                                        color: 'text.primary',
                                        mb: 3
                                    }}
                                >
                                    Configure Access Review
                                </mui.Typography>

                                <mui.Typography 
                                    variant="body2" 
                                    align="center" 
                                    sx={{ 
                                        mb: 4, 
                                        color: 'text.secondary',
                                        maxWidth: '90%',
                                        mx: 'auto'
                                    }}
                                >
                                    Setting up access reviews for <mui.Box component="span" sx={{ fontWeight: 600, color: 'primary.main' }}>{selectedApp?.APP_NAME}</mui.Box>
                                </mui.Typography>
                                
                                <mui.FormControl 
                                    fullWidth 
                                    sx={{ 
                                        mb: 3,
                                        '& label': {
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            mb: 0.5
                                        }
                                    }}
                                >
                                    <mui.Typography 
                                        variant="subtitle2" 
                                        color="text.primary" 
                                        sx={{ 
                                            mb: 1,
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <mui.Box 
                                            component="span" 
                                            sx={{ 
                                                width: 8, 
                                                height: 8, 
                                                backgroundColor: 'primary.main', 
                                                borderRadius: '50%', 
                                                display: 'inline-block',
                                                mr: 1
                                            }} 
                                        />
                                        Review Frequency
                                    </mui.Typography>
                                    <mui.Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 2 }}
                                    >
                                        Select 'One-time Review' for a single review, or choose a recurring frequency.
                                    </mui.Typography>
                                    <MultipleSelect
                                        isMultiSelect={false}
                                        placeholderText="Select Frequency"
                                        selectedOptions={selectedFrequency}
                                        selectOptions={frequency}
                                        value={selectedFrequency}
                                        handleChange={handleFrequencyChange}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 20000,
                                            }),
                                            control: (base) => ({
                                                ...base,
                                                zIndex: 10000,
                                                borderRadius: '8px',
                                                padding: '2px',
                                                boxShadow: 'none',
                                                border: '1px solid #e0e0e0',
                                                '&:hover': {
                                                    border: '1px solid #3949ab',
                                                }
                                            }),
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                </mui.FormControl>
                                
                                <mui.FormControl 
                                    fullWidth 
                                    sx={{ 
                                        mb: 3,
                                        '& label': {
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            mb: 0.5
                                        }
                                    }}
                                >
                                    <mui.Typography 
                                        variant="subtitle2" 
                                        color="text.primary" 
                                        sx={{ 
                                            mb: 1,
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <mui.Box 
                                            component="span" 
                                            sx={{ 
                                                width: 8, 
                                                height: 8, 
                                                backgroundColor: 'primary.main', 
                                                borderRadius: '50%', 
                                                display: 'inline-block',
                                                mr: 1
                                            }} 
                                        />
                                        First Review Start Date
                                    </mui.Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DemoContainer components={['DatePicker']}>
                                            <DatePicker
                                                value={isValidStartDate ? startDate : dayjs()}
                                                defaultValue={dayjs()}
                                                sx={{ 
                                                    width: '100%',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                    }
                                                }}
                                                onChange={handleDateChange}
                                                slotProps={{
                                                    textField: {
                                                        helperText: !isValidStartDate ? "Valid date required (YYYY-MM-DD)" : "",
                                                        error: !isValidStartDate,
                                                        required: true
                                                    },
                                                    field: {
                                                        clearable: false,
                                                        onClear: () => null // Prevent clearing
                                                    }
                                                }}
                                                disableMaskedInput
                                            />
                                        </DemoContainer>
                                    </LocalizationProvider>
                                </mui.FormControl>
                                
                                <mui.FormControl 
                                    fullWidth 
                                    sx={{ 
                                        mb: 3,
                                        '& label': {
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            mb: 0.5
                                        }
                                    }}
                                >
                                    <mui.Typography 
                                        variant="subtitle2" 
                                        color="text.primary" 
                                        sx={{ 
                                            mb: 1,
                                            fontWeight: 500,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <mui.Box 
                                            component="span" 
                                            sx={{ 
                                                width: 8, 
                                                height: 8, 
                                                backgroundColor: 'primary.main', 
                                                borderRadius: '50%', 
                                                display: 'inline-block',
                                                mr: 1
                                            }} 
                                        />
                                        Days to Complete Review
                                    </mui.Typography>
                                    <NormalTextField
                                        type="number"
                                        name="DAYS_TO_COMPLETE"
                                        value={uarFile.DAYS_TO_COMPLETE || ''}
                                        onChange={handleInputChange}
                                        fullWidth
                                        InputProps={{
                                            endAdornment: <mui.InputAdornment position="end">days</mui.InputAdornment>,
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            }
                                        }}
                                    />
                                </mui.FormControl>

                                <mui.Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-end', 
                                        gap: 2, 
                                        mt: 4,
                                        pt: 3,
                                        borderTop: '1px solid',
                                        borderColor: 'divider'
                                    }}
                                >
                                    <mui.Button 
                                        onClick={handleCloseSetupModal} 
                                        variant="outlined"
                                        color="inherit"
                                        sx={{ 
                                            borderRadius: 2,
                                            px: 3
                                        }}
                                    >
                                        Cancel
                                    </mui.Button>
                                    <mui.Button 
                                        onClick={createUARData} 
                                        variant="contained" 
                                        color="primary"
                                        disabled={!selectedFrequency || !isValidStartDate || !uarFile.DAYS_TO_COMPLETE}
                                        sx={{ 
                                            borderRadius: 2, 
                                            px: 3,
                                            boxShadow: '0 4px 10px rgba(73, 86, 221, 0.2)',
                                            background: 'linear-gradient(90deg, #3949ab 0%, #4f66d8 100%)',
                                            '&:hover': {
                                                background: 'linear-gradient(90deg, #3949ab 0%, #5c73e6 100%)',
                                                boxShadow: '0 6px 15px rgba(73, 86, 221, 0.3)',
                                                transform: 'translateY(-1px)'
                                            },
                                            '&.Mui-disabled': {
                                                background: '#e0e0e0',
                                                boxShadow: 'none'
                                            },
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        Create Review
                                    </mui.Button>
                                </mui.Box>
                            </mui.Box>
                        }
                        footer={null}
                    />

                    {/* Snackbar for notifications */}
                    <DynamicSnackbar
                        open={snackbarOpen}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                        onClose={handleSnackbarClose}
                    />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    // Effect to clean up selectedApp when both modals are closed
    useEffect(() => {
        if (!openCreateModal && !openSetupModal) {
            // Reset selectedApp only if both modals are closed
            setTimeout(() => {
                // Use timeout to avoid flickering state changes
                setSelectedApp(null);
            }, 300);
        }
    }, [openCreateModal, openSetupModal]);

    // Effect to set default date when setup modal is opened
    useEffect(() => {
        if (openSetupModal) {
            // If the start date is not set or invalid when modal opens, set it to today
            if (!uarFile.START_DATE || !isValidStartDate) {
                const today = dayjs();
                const formattedToday = today.format('YYYY-MM-DD');
                
                setUARFile(prev => ({
                    ...prev,
                    START_DATE: formattedToday
                }));
                
                console.log("Default date set to today:", formattedToday);
            }
        }
    }, [openSetupModal]);

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <Suspense fallback={
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100vh',
                        backgroundColor: '#f8fafc'
                    }}
                >
                    <LogoLoader 
                        size={80} 
                        message="Loading Applications..." 
                        duration={1.5} 
                    />
                </mui.Box>
            }>
                <div>
                    <SysOwnerSideBar mainContent={customMainContent} />
                </div>
            </Suspense>
        </ErrorBoundary>
    );
};

export default UserAccessReview;